import * as Sentry from '@sentry/react';

import { BundleInfo, CapacitorUpdater, LatestVersion } from '@capgo/capacitor-updater';
import mixpanel from 'mixpanel-browser';

import { useAnalytics } from './useAnalytics';

let availableUpdateBundle: BundleInfo;
const DEFAULT_CAPGO_CHANNEL = 'prod-1.4.x';

CapacitorUpdater.addListener('updateAvailable', async (res) => {
  try {
    mixpanel.track('CapacitorUpdater.updateAvailable', res);
    availableUpdateBundle = res.bundle;
  } catch (error) {
    Sentry.captureException(error);
  }
});

export const useCapacitorUpdateAvailable = () => {
  const { track } = useAnalytics();

  const updateApp = async () => {
    track('App Update Requested', {
      availableUpdateBundle,
    });

    if (availableUpdateBundle) {
      try {
        await CapacitorUpdater.set(availableUpdateBundle);
        availableUpdateBundle = undefined;
      } catch (error) {
        Sentry.captureException(error);
        await CapacitorUpdater.reload();
      }
    }
  };

  return {
    isCapacitorUpdateAvailable: !!availableUpdateBundle,
    updateApp,
  };
};

const handleFetchAndUpdateApp = async (latestVersion: LatestVersion, depth = 0) => {
  try {
    const latestVersionBundleInfo = await CapacitorUpdater.download({
      url: latestVersion.url,
      version: latestVersion.version,
      sessionKey: latestVersion.sessionKey,
      checksum: latestVersion.checksum,
    });

    try {
      await CapacitorUpdater.set(latestVersionBundleInfo);
    } catch (error) {
      Sentry.captureException(error);
      await CapacitorUpdater.reload();
    }
  } catch (error) {
    Sentry.captureException(error);

    if (depth < 3) {
      await handleFetchAndUpdateApp(latestVersion, depth + 1);
      return;
    }

    window.location.reload();
  }
};

export const fetchAndUpdateApp = async () => {
  try {
    const currentVersion = await CapacitorUpdater.current();
    const latestVersion = await CapacitorUpdater.getLatest({ channel: DEFAULT_CAPGO_CHANNEL });

    if (latestVersion?.url && currentVersion.bundle.version !== latestVersion.version) {
      await handleFetchAndUpdateApp(latestVersion);
    }
  } catch (error) {
    Sentry.captureException(error);
    window.location.reload();
  }
};
